import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, CreditCard, Loader2, Shield, MapPin, Calendar, Package, Wallet, Tag, X, Gift } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

export default function AppCheckout() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuth();
  const { clearCart } = useCart();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);
  const [payMethod, setPayMethod] = useState('card'); // 'card' or 'wallet'
  const [promoInput, setPromoInput] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    setApplyingPromo(true);
    try {
      const r = await fetch(`${API_URL}/api/orders/${orderId}/apply-promo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: promoInput.trim().toUpperCase() })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Invalid code');
      toast.success(d.message);
      setPromoInput('');
      if (d.mode === 'wallet') {
        // Rand voucher loaded to wallet — refresh balance & switch to wallet payment
        setWalletBalance(d.new_balance);
        setPayMethod('wallet');
      } else {
        fetchOrder();
      }
    } catch (e) { toast.error(e.message); }
    finally { setApplyingPromo(false); }
  };

  const handleRemovePromo = async () => {
    try {
      const r = await fetch(`${API_URL}/api/orders/${orderId}/remove-promo`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Failed'); }
      toast.success('Promo removed');
      fetchOrder();
    } catch (e) { toast.error(e.message); }
  };

  const fetchOrder = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/orders/${orderId}`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      if (!r.ok) throw new Error('Order not found');
      const data = await r.json();
      setOrder(data);
      if (data.payment_status === 'paid') clearCart();
    } catch (error) {
      toast.error(error.message);
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  }, [orderId, token, clearCart, navigate]);

  const verifyPayment = useCallback(async (reference) => {
    try {
      const r = await fetch(`${API_URL}/api/payments/verify/${reference}`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      if (!r.ok) throw new Error('Failed to verify payment');
      const data = await r.json();
      setCheckingStatus(false);
      if (data.status === 'success') {
        clearCart();
        toast.success('Payment successful!');
        window.history.replaceState({}, document.title, window.location.pathname);
        fetchOrder();
      } else {
        toast.error('Payment verification failed');
        fetchOrder();
      }
    } catch {
      setCheckingStatus(false);
      toast.error('Error verifying payment');
      fetchOrder();
    }
  }, [token, clearCart, fetchOrder]);

  // Fetch wallet balance
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/wallet/balance`, {
      headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
    }).then(r => r.ok ? r.json() : null).then(d => {
      if (d) setWalletBalance(d.balance);
    }).catch(() => {});
  }, [token]);

  useEffect(() => {
    const payment = searchParams.get('payment');
    const reference = searchParams.get('reference') || searchParams.get('trxref');
    if ((payment === 'callback' || reference) && reference) {
      // Returning from Paystack — verify payment
      setCheckingStatus(true);
      verifyPayment(reference);
    } else {
      fetchOrder();
    }
  }, [orderId, verifyPayment, fetchOrder, searchParams]);

  // Poll order status every 5s when order is pending — auto-detect webhook updates
  useEffect(() => {
    if (!order || order.payment_status === 'paid') return;
    const poll = setInterval(async () => {
      try {
        const r = await fetch(`${API_URL}/api/orders/${orderId}`, {
          headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
        });
        if (r.ok) {
          const d = await r.json();
          if (d.payment_status === 'paid') {
            setOrder(d);
            clearCart();
            toast.success('Payment confirmed!');
            clearInterval(poll);
          }
        }
      } catch (e) { /* silent poll */ }
    }, 5000);
    return () => clearInterval(poll);
  }, [order, orderId, token, clearCart]);


  // Pay with card — redirect to Paystack hosted checkout (no popup)
  const handleCardPayment = async () => {
    setPaying(true);
    try {
      const initResponse = await fetch(`${API_URL}/api/payments/initialize?order_id=${orderId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ origin_url: window.location.origin })
      });
      const initData = await initResponse.json();
      if (!initResponse.ok) throw new Error(initData.detail || 'Failed to initialize payment');

      // Always redirect to Paystack hosted checkout — most reliable, no popup issues
      window.location.href = initData.authorization_url;
    } catch (error) {
      toast.error(error.message);
      setPaying(false);
    }
  };

  // Pay with wallet
  const handleWalletPayment = async () => {
    setPaying(true);
    try {
      const r = await fetch(`${API_URL}/api/wallet/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ order_id: orderId })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Wallet payment failed');
      clearCart();
      toast.success('Paid from wallet!');
      fetchOrder();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setPaying(false);
    }
  };

  const handlePayment = () => {
    if (payMethod === 'wallet') handleWalletPayment();
    else handleCardPayment();
  };

  if (loading || checkingStatus) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">
            {checkingStatus ? 'Verifying your payment...' : 'Loading order...'}
          </p>
        </div>
      </div>
    );
  }

  if (!order) return null;

  const isPaid = order.payment_status === 'paid';
  const canUseWallet = walletBalance !== null && walletBalance >= (order.total_amount || 0);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="app-checkout">
      {/* Sticky header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="flex items-center gap-4 px-4 py-3 max-w-2xl mx-auto">
          <button onClick={() => navigate(isPaid ? '/orders' : -1)}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
            <ArrowLeft className="h-5 w-5 text-gray-700" />
          </button>
          <h1 className="font-heading text-lg font-bold text-gray-900">
            {isPaid ? 'Order Confirmed' : 'Checkout'}
          </h1>
          {!isPaid && (
            <span className="ml-auto text-xs text-gray-400 font-medium">
              #{order.order_id?.slice(-8).toUpperCase()}
            </span>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto pb-36">
        {isPaid ? (
          /* ── SUCCESS STATE ── */
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="px-4 py-8">
            <div className="text-center mb-8">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
                className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </motion.div>
              <h2 className="text-2xl font-black text-gray-900 mb-2">Order Placed!</h2>
              <p className="text-gray-500">Your food is on its way to the kitchen</p>
            </div>

            <div className="bg-white rounded-2xl p-5 mb-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Order ID</span>
                <span className="font-mono font-bold text-primary text-sm">#{order.order_id?.slice(-8).toUpperCase()}</span>
              </div>
              <div className="space-y-3">
                {order.items?.map((item, idx) => (
                  <div key={item.item_id || idx} className="flex items-center gap-3">
                    {item.image_url && <img src={item.image_url} alt={item.name} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">{item.name}</p>
                      <p className="text-xs text-gray-500">x{item.quantity}</p>
                    </div>
                    <p className="font-semibold text-gray-900 text-sm">R{(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
              {order.collection_code && (
                <div className="mt-4 p-4 bg-primary/5 rounded-xl text-center">
                  <p className="text-xs text-primary font-medium mb-1">Collection Code</p>
                  <p className="font-mono text-3xl font-black text-primary tracking-wider">{order.collection_code}</p>
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between">
                <span className="font-bold text-gray-900">Total Paid</span>
                <span className="font-black text-primary text-lg">R{order.total_amount?.toFixed(2)}</span>
              </div>
            </div>

            <div className="space-y-3">
              <button onClick={() => navigate('/orders')} className="w-full h-12 bg-primary text-white rounded-2xl font-semibold text-sm">
                View My Orders
              </button>
              <button onClick={() => navigate('/menu')} className="w-full h-12 bg-gray-100 text-gray-700 rounded-2xl font-semibold text-sm">
                Order again
              </button>
              <button onClick={() => navigate('/profile?action=buy-voucher')}
                className="w-full h-12 flex items-center justify-center gap-2 text-sm text-primary font-semibold"
                data-testid="checkout-gift-voucher">
                <Gift className="h-4 w-4" /> Gift a voucher to a colleague
              </button>
            </div>
          </motion.div>
        ) : (
          /* ── CHECKOUT STATE ── */
          <div className="px-4 py-4 space-y-4">
            {/* Items */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
              <div className="px-5 py-3 border-b border-gray-100">
                <h2 className="font-bold text-gray-900">Your order</h2>
              </div>
              <div className="divide-y divide-gray-50">
                {order.items?.map((item, idx) => (
                  <div key={item.item_id || idx} className="flex items-center gap-4 px-5 py-4">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-gray-100 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm">{item.name}</p>
                      {item.selected_sides?.length > 0 && (
                        <p className="text-xs text-gray-500 mt-0.5">+ {item.selected_sides.map(s => s.name).join(', ')}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-bold text-gray-900 text-sm flex-shrink-0">R{(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Delivery */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <h2 className="font-bold text-gray-900 mb-3">Delivery details</h2>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Delivery date</p>
                    <p className="font-semibold text-gray-900 text-sm">{order.delivery_date || 'Not specified'}</p>
                  </div>
                </div>
                {(order.delivery_address_name || order.delivery_address) && (
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Deliver to</p>
                      <p className="font-semibold text-gray-900 text-sm">{order.delivery_address_name || order.delivery_address}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Payment method selector */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <h2 className="font-bold text-gray-900 mb-3">Payment method</h2>
              <div className="space-y-2">
                <button onClick={() => setPayMethod('card')}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-colors ${payMethod === 'card' ? 'border-primary bg-primary/5' : 'border-gray-200'}`}>
                  <CreditCard className={`h-5 w-5 ${payMethod === 'card' ? 'text-primary' : 'text-gray-400'}`} />
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm text-gray-900">Pay with Card</p>
                    <p className="text-xs text-gray-500">Visa, Mastercard via Paystack</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 ${payMethod === 'card' ? 'border-primary bg-primary' : 'border-gray-300'}`}>
                    {payMethod === 'card' && <div className="w-full h-full rounded-full flex items-center justify-center"><div className="w-2 h-2 bg-white rounded-full" /></div>}
                  </div>
                </button>
                <button onClick={() => canUseWallet && setPayMethod('wallet')}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-colors ${payMethod === 'wallet' ? 'border-primary bg-primary/5' : 'border-gray-200'} ${!canUseWallet ? 'opacity-50' : ''}`}>
                  <Wallet className={`h-5 w-5 ${payMethod === 'wallet' ? 'text-primary' : 'text-gray-400'}`} />
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm text-gray-900">Pay from Wallet</p>
                    <p className="text-xs text-gray-500">
                      {walletBalance !== null ? `Balance: R${walletBalance.toFixed(2)}` : 'Loading...'}
                      {walletBalance !== null && !canUseWallet && ' (Insufficient)'}
                    </p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 ${payMethod === 'wallet' ? 'border-primary bg-primary' : 'border-gray-300'}`}>
                    {payMethod === 'wallet' && <div className="w-full h-full rounded-full flex items-center justify-center"><div className="w-2 h-2 bg-white rounded-full" /></div>}
                  </div>
                </button>
              </div>
              {walletBalance !== null && !canUseWallet && (
                <button
                  onClick={() => navigate('/profile?wallet=topup')}
                  className="mt-3 w-full flex items-center justify-between gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-left"
                  data-testid="topup-nudge">
                  <div className="flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-amber-600" />
                    <div>
                      <p className="text-xs font-semibold text-amber-800">Wallet short by R{(((order.total_amount || 0) - (walletBalance || 0))).toFixed(2)}</p>
                      <p className="text-[11px] text-amber-700">Top up to pay instantly without a card</p>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-amber-700 whitespace-nowrap">Top up →</span>
                </button>
              )}
            </div>

            {/* Payment summary */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <h2 className="font-bold text-gray-900 mb-3">Payment summary</h2>
              <div className="space-y-2.5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal ({order.items?.length} items)</span>
                  <span className="font-medium text-gray-900">R{order.subtotal?.toFixed(2) || order.total_amount?.toFixed(2)}</span>
                </div>
                {order.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600">Loyalty discount</span>
                    <span className="font-medium text-green-600">-R{order.discount?.toFixed(2)}</span>
                  </div>
                )}
                {order.promo_discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600 flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> Promo {order.promo_code}</span>
                    <span className="font-medium text-green-600">-R{order.promo_discount?.toFixed(2)}</span>
                  </div>
                )}
                {order.delivery_fee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Delivery fee</span>
                    <span className="font-medium text-gray-900">R{order.delivery_fee?.toFixed(2)}</span>
                  </div>
                )}
                {order.service_fee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Service fee</span>
                    <span className="font-medium text-gray-900">R{order.service_fee?.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-gray-100 pt-3 flex justify-between">
                  <span className="font-bold text-gray-900">Total</span>
                  <span className="font-black text-primary text-xl">R{order.total_amount?.toFixed(2)}</span>
                </div>
              </div>

              {/* Promo code */}
              {!isPaid && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  {order.promo_code ? (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-3 py-2.5" data-testid="promo-applied">
                      <span className="text-sm font-medium text-green-700 flex items-center gap-1.5"><Tag className="h-4 w-4" /> {order.promo_code} applied</span>
                      <button onClick={handleRemovePromo} className="text-green-700" data-testid="promo-remove"><X className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          value={promoInput}
                          onChange={e => setPromoInput(e.target.value.toUpperCase())}
                          placeholder="Promo code"
                          className="w-full h-11 pl-9 pr-3 bg-gray-50 border border-gray-200 rounded-xl text-sm uppercase focus:ring-2 focus:ring-primary/20 outline-none"
                          data-testid="promo-input"
                        />
                      </div>
                      <button
                        onClick={handleApplyPromo}
                        disabled={applyingPromo || !promoInput.trim()}
                        className="h-11 px-4 bg-secondary-900 text-white rounded-xl text-sm font-semibold disabled:opacity-50"
                        data-testid="promo-apply"
                      >
                        {applyingPromo ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 rounded-2xl border border-blue-100">
              <Shield className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <p className="text-xs text-blue-700">
                <strong>Secure payment</strong> — Your card details are encrypted and never stored on our servers
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Fixed pay button */}
      {!isPaid && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-100 px-4 pt-4 pb-8 z-40">
          <div className="max-w-2xl mx-auto">
            <button onClick={handlePayment} disabled={paying}
              className="w-full h-14 bg-primary text-white rounded-2xl font-black text-base flex items-center justify-center gap-2.5 disabled:opacity-50 transition-all active:scale-[0.98] shadow-lg shadow-primary/30"
              data-testid="pay-btn">
              {paying ? (
                <><Loader2 className="h-5 w-5 animate-spin" /> Processing...</>
              ) : payMethod === 'wallet' ? (
                <><Wallet className="h-5 w-5" /> Pay R{order.total_amount?.toFixed(2)} from Wallet</>
              ) : (
                <><CreditCard className="h-5 w-5" /> Pay R{order.total_amount?.toFixed(2)}</>
              )}
            </button>
            <p className="text-center text-xs text-gray-400 mt-2">
              {payMethod === 'wallet' ? 'Instant payment from your wallet balance' : 'Secured by Paystack · SSL encrypted'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
